import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import _ from 'lodash';
import {v4} from 'uuid';
import {Reports} from '../../config/reportTypes';
import {
  DocumentGroup,
  TimelineResponse,
} from '../../../../../api/types/reportingApi.type';
import {buildTreeItems} from '../../../../../utils/reportUtils';
import {TimelineIngestionType} from '../../../../../redux/slice/timelineGapReportSlice';
import {getSectionMeta} from '../../component/left-panel/components/ArrSourceSelectedSection';

dayjs.extend(utc);

export const toISO = (dateString: any) => {
  if (!dateString) return null;
  const d = new Date(dateString);

  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};
export const sourcesSectionKeys = [
  'SECURITYGROUPS',
  'SEARCHES',
  'ASSIGNMENTS',
  'FOLDERS',
  'TAGS',
  'INGESTION',
  'PROJECTS',
];

const SourceNameMap: {[key: string]: {name: string; responseKey: string}} = {
  SECURITYGROUPS: {name: 'Security Group', responseKey: 'securityGroupList'},
  SEARCHES: {name: 'Search', responseKey: 'searchTreeList'},
  ASSIGNMENTS: {name: 'Assignment', responseKey: 'assignmentTreeList'},
  FOLDERS: {name: 'Folder', responseKey: 'folderTreeList'},
  TAGS: {name: 'Tag', responseKey: 'tagTreeList'},
};

export type TimelineReportType = 'YEARLY' | 'MONTHLY' | 'DAILY';

const buildGroupListTree = (
  groups: DocumentGroup[],
  parent = '#',
  result: {id: string; text: string; parent: string; isGroup: boolean}[] = [],
  selectedDocTypes: Map<string, string>
) => {
  groups.forEach((group) => {
    const id = selectedDocTypes.has(group.id.toString())
      ? (selectedDocTypes.get(group.id.toString()) ?? '')
      : `${v4()}_${group.id}`;
    result.push({
      id: id,
      text: group.name,
      parent,
      isGroup: group.documentTypeList?.length > 0,
    });

    if (group.documentTypeList?.length > 0) {
      buildGroupListTree(
        group.documentTypeList as DocumentGroup[],
        id,
        result,
        selectedDocTypes
      );
    }
  });
  return result;
};

const getDefaultStartEndDate = () => {
  const today = new Date();

  const endDate = today.toISOString().split('T')[0];

  const start = new Date(today);
  start.setFullYear(start.getFullYear() - 5);
  const startDate = start.toISOString().split('T')[0];

  return {startDate, endDate};
};

const normalizeDate = (dateStr: string): string => {
  if (!dateStr) return dateStr;
  const separator = dateStr.includes('/') ? '/' : '-';
  const [year, month, day] = dateStr.split(separator);
  return `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
};

export const getSelectedIngestions = (data: TimelineIngestionType | null) => {
  if (!data) return [];
  return {
    INGESTION: [
      {
        sourceName: data.sourceType,
        source: [data.source.id.toString()],
        navigationSourceType: data.navigationSourceType,
      },
    ],
  };
};

export const getTimelineViewPayload = (
  type: TimelineReportType,
  timelinePanelData: any,
  filterValues: Record<string, any>,
  timelineResult: Record<string, any> | null,
  selectedTimelineDuration?: {[key: string]: number}[],
  isForExportReport: boolean = false,
  ingestion?: TimelineIngestionType | null
) => {
  const {startDate, endDate} = getDefaultStartEndDate();

  const payload: {[key: string]: any} = {
    CustodianList: [],
    CustodianNameList: [],
    DataAsOf: dayjs.utc(),
    DocumentTypeList: [],
    DocumentTypeNameList: [],
    DomainList: [],
    DomainNameList: [],
    StartDate: (filterValues.fromDate
      ? dayjs(filterValues.fromDate).format('YYYY-MM-DD')
      : normalizeDate(timelinePanelData?.fromDate ?? startDate)
    ).replace(/\//g, '-'),
    EndDate: (filterValues.toDate
      ? dayjs(filterValues.toDate).format('YYYY-MM-DD')
      : normalizeDate(timelinePanelData?.toDate ?? endDate)
    ).replace(/\//g, '-'),
    IncludeDupeCustodian: filterValues.includeDupeCustodian ?? false,
    SourceID: [],
    SourceList: [],
    SourceName: '',
    SourceType: '',
    SourceTypeCode:
      timelineResult?.timelineFilterResponse?.sourceTypeCode ?? 'TimeLine',
    TimeLineBy: filterValues?.timelineBy?.name ?? '',
    TimeLineByID: `${filterValues?.timelineBy?.id ?? ''}`,
    TimelineReportType: type,
    YearlyData: [],
    YearlySelectedPeriod:
      timelineResult?.timelineFilterResponse?.yearlySelectedPeriod ?? [],
    MonthlyData: [],
    MonthlySelectedPeriod:
      timelineResult?.timelineFilterResponse?.monthlySelectedPeriod ?? [],
    DailyData: [],
    DailySelectedPeriod:
      timelineResult?.timelineFilterResponse?.dailySelectedPeriod ?? [],
    navigationSourceType:
      timelineResult?.timelineFilterResponse?.navigationSourceType ?? '',
    totalDocumentToView:
      timelineResult?.timelineFilterResponse?.totalDocumentToView ?? 0,
    isShared: timelineResult?.timelineFilterResponse?.isShared ?? false,
    isSavedSearchSource:
      timelineResult?.timelineFilterResponse?.isSavedSearchSource ?? false,
    gapReportType: timelineResult?.timelineFilterResponse?.gapReportType ?? '',
    reportName: timelineResult?.timelineFilterResponse?.reportName ?? '',
    isDocumentCount:
      timelineResult?.timelineFilterResponse?.isDocumentCount ?? false,
  };

  if (timelineResult?.timelineFilterResponse?.docListID)
    payload.docListID = timelineResult?.timelineFilterResponse?.docListID;

  if (timelineResult?.timelineFilterResponse?.destinationContextTypeCode)
    payload.destinationContextTypeCode =
      timelineResult?.timelineFilterResponse?.destinationContextTypeCode;

  const timelineModel = timelinePanelData?.timelineModel ?? {};

  if (isForExportReport) {
    payload.YearlyData =
      timelineResult?.timelineFilterResponse?.yearlyData ?? [];
    payload.MonthlyData =
      timelineResult?.timelineFilterResponse?.monthlyData ?? [];
    payload.DailyData = timelineResult?.timelineFilterResponse?.dailyData ?? [];
  } else {
    switch (type) {
      case 'YEARLY':
        payload.YearlyData = [];
        payload.MonthlyData = [];
        payload.DailyData = [];
        break;
      case 'MONTHLY':
        payload.YearlyData =
          timelineResult?.timelineFilterResponse?.yearlyData ?? [];
        break;
      case 'DAILY':
        payload.YearlyData =
          timelineResult?.timelineFilterResponse?.yearlyData ?? [];
        payload.MonthlyData =
          timelineResult?.timelineFilterResponse?.monthlyData ?? [];
        payload.DailyData =
          timelineResult?.timelineFilterResponse?.dailyData ?? [];
        break;
      default:
        break;
    }
  }

  if (
    type === 'MONTHLY' &&
    selectedTimelineDuration &&
    selectedTimelineDuration?.length > 0
  ) {
    payload.YearlySelectedPeriod = selectedTimelineDuration;
  }

  if (
    type === 'DAILY' &&
    selectedTimelineDuration &&
    selectedTimelineDuration?.length > 0
  ) {
    payload.MonthlySelectedPeriod = selectedTimelineDuration;
  }

  //Set Document Types in payload
  if (filterValues.selectedDocumentType) {
    const documentGroupList = _.get(
      timelineModel,
      'documentGroupList',
      []
    ) as DocumentGroup[];
    const documentTypeNameList: string[] = [];
    const actualSelectedDocTypeIds = (
      filterValues.selectedDocumentType ?? []
    ).map((id: string) => id.split('_')[1]);

    documentGroupList.forEach((sg) => {
      if (sg.documentTypeList && sg.documentTypeList.length > 0) {
        sg.documentTypeList.forEach((dt) => {
          if (actualSelectedDocTypeIds.includes(dt.id.toString())) {
            documentTypeNameList.push(dt.name);
          }
        });
      } else if (actualSelectedDocTypeIds.includes(sg.id.toString())) {
        documentTypeNameList.push(sg.name);
      }
    });
    payload.DocumentTypeList = actualSelectedDocTypeIds;
    payload.DocumentTypeNameList = documentTypeNameList;
  }

  // Custodian data setting in payload
  if (filterValues.custodian) {
    payload.CustodianList = (filterValues.custodian ?? []).map(
      (item: {id: number; name: string}) => item.id.toString()
    );
    payload.CustodianNameList = (filterValues.custodian ?? []).map(
      (item: {id: number; name: string}) => item.name
    );
  }

  // Set sources in payload
  sourcesSectionKeys.forEach((sourceKey) => {
    const sourceValue = _.get(filterValues, `${sourceKey}`, []);
    if (sourceValue.length > 0) {
      const selectedSource = sourceValue[0];
      const sourceMap = _.get(SourceNameMap, `${sourceKey}`, {
        name: '',
        responseKey: '',
      });

      if (sourceKey === 'INGESTION' && ingestion) {
        payload.SourceID = [ingestion.source.id];
        payload.SourceName = ingestion.sourceName;
        payload.SourceType = ingestion.sourceType;
        payload.navigationSourceType = ingestion.navigationSourceType;
      } else {
        payload.SourceID = selectedSource.source;
        payload.SourceName = sourceMap.name;
        payload.SourceType = sourceKey;
      }

      const sourceList: string[] = [];
      if (sourceKey === 'INGESTION') {
        sourceList.push(ingestion?.source.name || '');
      } else if (sourceKey === 'SECURITYGROUPS') {
        const securityGroupList = _.get(
          timelineModel,
          sourceMap.responseKey,
          []
        ) as {id: number; name: string}[];
        securityGroupList.forEach((sg) => {
          if (selectedSource.source.includes(sg.id.toString())) {
            sourceList.push(sg.name);
          }
        });
      } else {
        const treeList = _.get(timelineModel, sourceMap.responseKey, []) as {
          id: string;
          text: string;
        }[];
        let foundCount = 0;
        treeList.forEach((treeItem) => {
          if (selectedSource.source.includes(treeItem.id.toString())) {
            foundCount++;
            sourceList.push(treeItem.text);
            if (foundCount === selectedSource.source.length) {
              return;
            }
          }
        });
      }
      payload.SourceList = sourceList;
    }
  });
  return payload;
};

export const getBulkActionExecutePayload = (data: {
  operationType?: string;
  sourceDetails?: {[key: string]: any};
}): {
  selectedList: {[key: string]: any};
  destinationList: {[key: string]: any};
} => {
  const {sourceDetails = {}} = data;

  const updatedSourceDetails: {[key: string]: any} = {...sourceDetails};

  if (
    sourceDetails.SourceID &&
    Array.isArray(sourceDetails.SourceID) &&
    sourceDetails.SourceID.length > 0
  ) {
    updatedSourceDetails.SourceID = parseInt(sourceDetails.SourceID[0]);
  }
  return {selectedList: updatedSourceDetails, destinationList: {}};
};

export const getBulkActionSourceDetails = (
  sourceDetails: {
    [key: string]: any;
  },
  forAPI?: string
) => {
  const updatedSourceDetails: {[key: string]: any} = {...sourceDetails};
  if (
    forAPI === 'getBulkDocListId' &&
    sourceDetails.SourceID &&
    Array.isArray(sourceDetails.SourceID) &&
    sourceDetails.SourceID.length > 0
  ) {
    updatedSourceDetails.SourceID = parseInt(sourceDetails.SourceID[0]);
  }
  return updatedSourceDetails;
};
export const updateApplyButtonState = (
  report: Reports,
  filterValues: Record<string, any>,
  isQueryRun: boolean
) => {
  const isTimelineChanged = !!filterValues.timelineBy;

  const isFromDateChanged =
    !!filterValues.fromDate && String(filterValues.fromDate).trim() !== '';

  const isToDateChanged =
    !!filterValues.toDate && String(filterValues.toDate).trim() !== '';

  const isDateChanged = isFromDateChanged && isToDateChanged;

  const isCustodianChanged =
    Array.isArray(filterValues.custodian) && filterValues.custodian.length > 0;

  const isSourcesChanged = sourcesSectionKeys.some((key) => {
    const val = filterValues[key as keyof typeof filterValues];
    return Array.isArray(val) && val.length > 0;
  });

  const isDocumentTypeChanged =
    Array.isArray(filterValues.selectedDocumentType) &&
    filterValues.selectedDocumentType.length > 0;

  const isIncludeDupeChanged = !!filterValues.includeDupeCustodian;

  const enableApply = isSourcesChanged && isTimelineChanged;
  const enableReset =
    isTimelineChanged ||
    isQueryRun ||
    isDateChanged ||
    isCustodianChanged ||
    isSourcesChanged ||
    isDocumentTypeChanged ||
    isIncludeDupeChanged;

  const updatedActions = report.config.selectionCriteria.actions.map(
    (action) => {
      if (action.actionKey === 'applyChanges') {
        return {...action, disabled: !enableApply};
      }
      if (action.actionKey === 'reset') {
        return {...action, disabled: !enableReset};
      }
      return action;
    }
  );

  return {
    ...report,
    config: {
      ...report.config,
      selectionCriteria: {
        ...report.config.selectionCriteria,
        actions: updatedActions,
      },
    },
  };
};

export const mergeTimelineApiDataIntoConfig = (
  report: Reports,
  apiData: TimelineResponse,
  inititalApplyFilters?: Record<string, any> | null,
  isStoreReportConfig?: boolean,
  savedReportStateData?: any
) => {
  const config = report.config;

  const updatedSections = config?.sections?.map((section) => ({
    ...section,
    filters: section.filters.map((filter) => ({
      ...filter,
      components: filter.components.map((comp) => {
        if (comp.input_type === 'dropdown') {
          if (filter.id === 'timelineBy') {
            let initialValue = inititalApplyFilters?.timelineBy ?? null;
            if (savedReportStateData?.timelineFilter) {
              initialValue = {
                id: Number(
                  savedReportStateData?.timelineFilter?.timeLineByID ?? 0
                ),
                name: savedReportStateData?.timelineFilter?.timeLineBy ?? '',
              };
            }
            return {
              ...comp,
              dropdownItems: apiData?.timelineModel?.timelineByList ?? [],
              initialValues: initialValue,
            };
          }
        }

        if (filter.id === 'custodian') {
          if (
            comp.input_type === 'multiselectdropdown' &&
            comp.key === 'custodian'
          ) {
            let initialValue = inititalApplyFilters?.custodian ?? [];
            if (savedReportStateData?.timelineFilter) {
              const custodianNameList =
                savedReportStateData?.timelineFilter?.custodianNameList ?? [];
              initialValue =
                savedReportStateData?.timelineFilter?.custodianList?.map(
                  (id: string, index: number) => ({
                    id: Number(id),
                    name: _.get(custodianNameList, index, ''),
                  })
                ) ?? [];
            }
            return {
              ...comp,
              options: apiData?.timelineModel?.custodianList ?? [],
              initialValues: initialValue,
            };
          }

          if (
            comp.input_type === 'checkbox' &&
            comp.key === 'includeDupeCustodian'
          ) {
            let initialValue =
              inititalApplyFilters?.includeDupeCustodian ?? false;
            if (savedReportStateData?.timelineFilter) {
              initialValue =
                savedReportStateData?.timelineFilter?.includeDupeCustodian ??
                false;
            }
            return {
              ...comp,
              initialValues: initialValue,
            };
          }
        }

        if (
          comp.input_type === 'multiSelectTreeViewList' &&
          filter.id === 'documentTypeSection'
        ) {
          let initialValues = inititalApplyFilters?.selectedDocumentType ?? [];
          if (savedReportStateData?.timelineFilter) {
            initialValues =
              savedReportStateData?.timelineFilter?.documentTypeList ?? [];
          }

          const selectedDocTypes = new Map<string, string>([]);
          initialValues.forEach((val: string) => {
            selectedDocTypes.set(val, `${v4()}_${val}`);
          });

          const transformedData = isStoreReportConfig
            ? comp.treeData
            : buildTreeItems(
                buildGroupListTree(
                  apiData?.timelineModel?.documentGroupList,
                  '#',
                  [],
                  selectedDocTypes
                )
              );
          return {
            ...comp,
            treeData: transformedData,
            initialValues: Array.from(selectedDocTypes.values()),
          };
        }

        if (comp.input_type === 'datePicker') {
          if (comp.key === 'fromDate') {
            let initialValue =
              inititalApplyFilters?.fromDate ?? apiData?.fromDate;

            if (savedReportStateData?.timelineFilter?.startDate) {
              initialValue =
                savedReportStateData?.timelineFilter?.startDate.split('T')[0];
            }
            return {
              ...comp,
              initialStartDate: normalizeDate(initialValue),
              initialValues: normalizeDate(initialValue),
            };
          }

          if (comp.key === 'toDate') {
            let initialValue = inititalApplyFilters?.toDate ?? apiData?.toDate;

            if (savedReportStateData?.timelineFilter?.endDate) {
              initialValue =
                savedReportStateData?.timelineFilter?.endDate.split('T')[0];
            }
            return {
              ...comp,
              initialEndDate: normalizeDate(initialValue),
              initialValues: normalizeDate(initialValue),
            };
          }
        }

        return comp;
      }),
    })),
  }));

  return {
    ...report,
    config: {
      ...config,
      sections: updatedSections,
    },
  };
};

export const getAppliedFilterValueObject = (
  report: Reports,
  savedReportState: {[key in string]: any}
) => {
  const filterObject: {[key: string]: any} = {};
  const timelineFilterResponse = savedReportState.timelineFilterResponse;

  const sourceMeta = getSectionMeta(
    timelineFilterResponse?.sourceType.toLowerCase() ?? ''
  );

  filterObject[timelineFilterResponse?.sourceType] = [
    {
      sourceName: sourceMeta.sourceName,
      navigationSourceType: sourceMeta.navigationSourceType,
      source: timelineFilterResponse.sourceID,
    },
  ];

  const config = report.config;

  config.sections.forEach((section) => {
    section.filters.forEach((filter) => {
      filter.components.forEach((comp) => {
        if (
          comp.input_type === 'dropdown' &&
          filter.id === 'timelineBy' &&
          comp.initialValues
        ) {
          filterObject.timelineBy = comp.initialValues;
        }

        if (filter.id === 'custodian') {
          if (
            comp.input_type === 'multiselectdropdown' &&
            comp.key === 'custodian' &&
            comp.initialValues
          ) {
            filterObject.custodian = comp.initialValues;
          }
        }

        if (
          comp.input_type === 'checkbox' &&
          comp.key === 'includeDupeCustodian'
        ) {
          filterObject.includeDupeCustodian = comp.initialValues;
        }

        if (
          comp.input_type === 'multiSelectTreeViewList' &&
          filter.id === 'documentTypeSection' &&
          comp.initialValues
        ) {
          filterObject.selectedDocumentType = comp.initialValues;
        }

        if (comp.input_type === 'datePicker') {
          if (comp.key === 'fromDate' && comp.initialStartDate) {
            filterObject.fromDate = comp.initialStartDate;
          }
          if (comp.key === 'toDate' && comp.initialEndDate) {
            filterObject.toDate = comp.initialEndDate;
          }
        }
      });
    });
  });
  return filterObject;
};
